import i18n from '@/localisation/i18n';
import { VBtn, VIcon } from 'vuetify/lib';
import { alignCenter, alignLeft, comparator, component } from './utils';
import { h } from 'vue';
import TraderUsersRiskLimitIcons from '@/modules/trader-admin/components/TraderUsersRiskLimitIcons.vue';
import TraderUsersActions from '@/modules/trader-admin/components/TraderUsersActions.vue';
import { ColDef } from 'ag-grid-enterprise';
import { formatAnyDate } from '@/utils/helpers/dates';
import { formatDuration, intervalToDuration, isAfter, subMilliseconds } from 'date-fns';
import { HEARTBEAT_INTERVAL } from '@/store/auth-store';
import { User } from '@/modules/user-accounts/models';

export function name(): ColDef<User> {
  return {
    field: 'name',
    colId: 'name',
    headerName: 'User',
    ...alignLeft(),
  };
}

export function company(): ColDef<User> {
  return {
    field: 'companyName',
    colId: 'companyName',
    headerName: 'Company Name',
    ...alignLeft(),
  };
}

export function companyDisplayId(): ColDef<User> {
  return {
    field: 'companyDisplayId',
    colId: 'companyDisplayId',
    headerName: 'Company ID',
    ...alignLeft(),
  };
}

export function email(): ColDef<User> {
  return {
    field: 'emailAddress',
    colId: 'emailAddress',
    headerName: 'Email',
    ...alignLeft(),
  };
}

export function role(): ColDef<User> {
  return {
    colId: 'userRole',
    headerName: 'Role',
    valueGetter: (params) => {
      if (!params.data?.roles[0]) return '';
      const currentRole = i18n.tc(params.data.roles[0]);
      if (
        params.data.accountStatus === 'pending-approval-for-roles' &&
        params.data.rolesPendingApproval?.[0]
      ) {
        return `${currentRole} ⇢ ${i18n.tc(params.data.rolesPendingApproval[0])}`;
      }
      return currentRole;
    },
    ...alignLeft(),
  };
}

export function traderAccountStatus(): ColDef<User> {
  return {
    field: 'accountStatus',
    colId: 'accountStatus',
    headerName: 'Account Status',
    valueFormatter: (params) => i18n.tc(`userAccounts.status.${params.value}`),
    ...alignLeft(),
  };
}

const brokerAccountStatusAdapter = component<{
  item: User;
  approve: (item: User) => void;
  hasBrokerComplianceRepRole: boolean;
}>((props) => () => {
  return h(
    VBtn,
    {
      props: { color: 'primary', xSmall: true },
      on: {
        click: () => props.approve(props.item),
      },
    },
    props.item.accountStatus === 'pending-approval'
      ? i18n.tc('adminApproveUser')
      : props.item.accountStatus === 'pending-approval-for-roles'
        ? i18n.tc('adminApproveRoles')
        : undefined
  );
});

export function brokerAccountStatus({
  approve,
  hasBrokerComplianceRepRole,
}: {
  approve: (item: User) => void;
  hasBrokerComplianceRepRole: boolean;
}): ColDef<User> {
  return {
    field: 'accountStatus',
    colId: 'accountStatus',
    headerName: 'Account Status',
    // we need to force it because autosized on first render is sometimes too small
    // i.e. when it starts with no button and we create a user, button doesn't fit
    minWidth: 200,
    valueFormatter: (params) => i18n.tc(`userAccounts.status.${params.value}`),
    cellRendererSelector: (params) => {
      if (
        hasBrokerComplianceRepRole &&
        ['pending-approval', 'pending-approval-for-roles'].includes(params.value)
      ) {
        return brokerAccountStatusAdapter({
          item: params.data as User,
          approve,
          hasBrokerComplianceRepRole,
        });
      }
    },
    ...alignLeft(),
  };
}

const tfaIsEnabledAdapter = component<{
  item?: User;
}>((props) => () => {
  return props.item === undefined
    ? undefined
    : h(VIcon, [props.item.tfaIsEnabled ? 'mdi-two-factor-authentication' : undefined]);
});

export function tfa(): ColDef<User> {
  return {
    field: 'tfaIsEnabled',
    colId: 'tfaIsEnabled',
    headerName: '2FA',
    cellRendererSelector: (params) => tfaIsEnabledAdapter({ item: params.data }),
    ...alignLeft(),
  };
}

const riskLimitsAdapter = component<{
  item?: User;
}>((props) => () => {
  return props.item === undefined
    ? undefined
    : h(TraderUsersRiskLimitIcons, { props: { item: props.item } });
});

export function riskLimits(): ColDef<User> {
  return {
    colId: 'riskLimits',
    headerName: 'Risk Limits',
    cellRendererSelector: (params) => riskLimitsAdapter({ item: params.data }),
    ...alignLeft(),
  };
}

const actionsAdapter = component<{
  item: User;
  deleteUser: (item: User) => void;
  disableUser: (item: User) => void;
  editUser: (item: User) => void;
  enableUser: (item: User) => void;
}>((props) => () => {
  return h(TraderUsersActions, {
    props: {
      item: props.item,
    },
    on: {
      'delete-user': props.deleteUser,
      'disable-user': props.disableUser,
      'edit-user': props.editUser,
      'enable-user': props.enableUser,
    },
  });
});

export function actions({
  deleteUser,
  disableUser,
  editUser,
  enableUser,
}: {
  deleteUser: (item: User) => void;
  disableUser: (item: User) => void;
  editUser: (item: User) => void;
  enableUser: (item: User) => void;
}): ColDef<User> {
  return {
    colId: 'actions',
    headerName: 'Actions',
    cellRendererSelector: (params) =>
      actionsAdapter({
        item: params.data as User,
        deleteUser,
        disableUser,
        editUser,
        enableUser,
      }),
    pinned: 'right',
    width: 150,
    lockVisible: true,
    ...alignCenter(),
  };
}

export function longCompanyName(): ColDef<User> {
  return {
    field: 'companyName',
    colId: 'companyName',
    headerName: 'Company Name',
    valueFormatter: (params) =>
      params.data === undefined
        ? ''
        : `${params.data.companyName} (${params.data.companyDisplayId})`,
    ...alignLeft(),
  };
}

export function tradingPermissions(): ColDef<User> {
  return {
    field: 'tradingPermissions',
    colId: 'tradingPermissions',
    headerName: 'Trading Permissions',
    valueFormatter: (params) =>
      params.data === undefined
        ? ''
        : params.data.tradingPermissions !== null
          ? i18n.tc(`tradingPermissions.options.${params.value}`)
          : i18n.tc(`tradingPermissions.inherit`),
    ...alignLeft(),
  };
}

export function lastLogin(): ColDef<User> {
  return {
    field: 'lastLoginAt',
    colId: 'lastLoginAt',
    headerName: 'Last Login',
    valueFormatter: (params) => formatAnyDate(params.value, 'hh:mm:ss MM/dd/yy'),
    comparator: comparator.date,
    ...alignLeft(),
  };
}

export function lastActive(): ColDef<User> {
  return {
    field: 'lastActive',
    colId: 'lastActive',
    headerName: 'Last Active',
    valueFormatter: (params) => {
      if (!params.value) {
        return '';
      }

      const now = new Date();
      const nowThreshold = subMilliseconds(now, HEARTBEAT_INTERVAL);

      if (isAfter(params.value, nowThreshold)) {
        return 'now';
      } else {
        return formatAnyDate(params.value, 'hh:mm:ss MM/dd/yy');
      }
    },
    comparator: comparator.date,
    ...alignLeft(),
  };
}

export function totalActiveTime(): ColDef<User> {
  return {
    field: 'totalActiveMinutes',
    colId: 'totalActiveMinutes',
    headerName: 'Total Active Time',
    valueFormatter: (params) => {
      const duration = intervalToDuration({ start: 0, end: params.value * 60 * 1000 });
      return formatDuration(duration);
    },
    comparator: comparator.number,
    ...alignLeft(),
  };
}
